import { useEffect, useState } from "react";
import { X, DownloadSimple, Share } from "@phosphor-icons/react";

const DISMISS_KEY = "freightpilot_pwa_install_dismissed_v1";
const DISMISS_DAYS = 14;

function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = new Date(raw);
    if (isNaN(at)) return false;
    const days = (Date.now() - at.getTime()) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

/**
 * Non-intrusive PWA install prompt.
 * - Chrome/Edge (Android + desktop): uses native `beforeinstallprompt`
 * - iOS Safari: shows a hint with the Share → Add to Home Screen steps
 * - Auto-hides if already installed, dismissed recently, or on non-mobile browsers where it doesn't add value
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null); // "native" | "ios"

  useEffect(() => {
    if (isRunningStandalone() || wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("native");
      // Delay showing so it doesn't crowd first paint
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS fallback (Safari never fires beforeinstallprompt)
    if (isIOS() && !window.navigator?.standalone) {
      setMode("ios");
      setTimeout(() => setVisible(true), 4000);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install FreightPilot AI"
      className="fixed left-4 right-4 bottom-24 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[65]"
    >
      <div className="rounded-2xl bg-[#0A0A0A]/95 backdrop-blur-md border border-white/10 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <img src="/pwa-icon-192.png" alt="" width="44" height="44" className="rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm">Install FreightPilot on your phone</div>
            {mode === "native" && (
              <p className="text-neutral-400 text-xs mt-1 leading-relaxed">
                One-tap install. Opens like a real app — no browser bar, home screen icon, faster launch.
              </p>
            )}
            {mode === "ios" && (
              <p className="text-neutral-400 text-xs mt-1 leading-relaxed">
                Tap <Share size={12} className="inline align-middle" /> in Safari, then <b className="text-white">Add to Home Screen</b>. That's it — you'll have the app icon.
              </p>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            data-testid="pwa-install-dismiss"
            className="shrink-0 -mt-1 -mr-1 p-1 text-neutral-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        {mode === "native" && (
          <div className="mt-3 flex gap-2">
            <button
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="flex-1 rounded-full px-4 py-2 text-xs font-medium border border-white/15 text-neutral-200 hover:text-white hover:border-white/30 transition-colors"
            >
              Later
            </button>
            <button
              onClick={install}
              data-testid="pwa-install-accept"
              className="flex-1 rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <DownloadSimple size={13} weight="bold" /> Install
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
